'use client';

import { Skeleton } from '@/components/ui/app-skeleton';
import { Panel } from '@/components/ui/panel';
import { cn } from '@/lib/ui/utils';

export interface FeaturedMarketHeroSkeletonProps {
  className?: string;
}

const OUTCOME_KEYS = ['o-0', 'o-1', 'o-2'] as const;

/**
 * Loading placeholder for {@link FeaturedMarketHero}. Mirrors the header +
 * three-column outcome sparkline grid at the same heights and gaps as the live
 * hero so swapping in real data produces zero layout shift. Hidden on mobile
 * to match the hero itself.
 */
export function FeaturedMarketHeroSkeleton(
  props: FeaturedMarketHeroSkeletonProps,
): React.ReactNode {
  const { className } = props;
  return (
    <Panel
      surface="solid"
      elevation="flat"
      className={cn('hidden md:flex h-full w-full flex-col gap-4 overflow-hidden p-5', className)}
      data-testid="featured-market-hero-skeleton"
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <Skeleton className="h-7 min-w-0 flex-1 rounded-item" />
          <div className="flex shrink-0 items-center gap-0">
            <Skeleton className="size-8 rounded-full" />
            <Skeleton className="size-8 rounded-full" />
            <span aria-hidden="true" className="mx-1 h-4 w-px shrink-0 bg-border-primary" />
            <Skeleton className="size-8 rounded-full" />
            <Skeleton className="size-8 rounded-full" />
          </div>
        </div>
        <Skeleton className="h-3 w-2/5 rounded-item" />
      </div>

      <div className="mt-2 grid grid-cols-3 items-stretch gap-2">
        {OUTCOME_KEYS.map((key) => (
          <div
            key={key}
            // Mirrors OutcomeSparklineCard's slot stack so the column height
            // matches byte-for-byte (zero hero CLS): label + display % + mcap
            // reserve, h-16 chart, flex-1 spacer, min-h-9 thesis, min-h-4 footer.
            className="flex h-full flex-col gap-3 rounded-panel-row border border-border-secondary p-3 shadow-none"
          >
            <div className="flex flex-col gap-1">
              <Skeleton className="h-5 w-24 rounded-item" />
              <Skeleton className="h-9 w-16 rounded-item" />
              {/* mcap reserve — matches the live card's always-reserved row */}
              <div className="h-4" aria-hidden="true" />
            </div>
            <Skeleton className="h-16 w-full rounded-item" />
            <div className="min-h-0 flex-1" aria-hidden="true" />
            <div className="flex min-h-9 flex-col gap-1">
              <Skeleton className="h-4 w-full rounded-item" />
              <Skeleton className="h-4 w-3/4 rounded-item" />
            </div>
            <div className="flex min-h-4 items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <Skeleton className="size-4 shrink-0 rounded-full" />
                <Skeleton className="h-3 w-16 rounded-item" />
              </div>
              <Skeleton className="h-3 w-8 shrink-0 rounded-item" />
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}
